import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  TextField
} from '@material-ui/core'
import { DatePicker, TimePicker } from '@material-ui/pickers'
import { Save } from '@material-ui/icons'
import { Formik } from 'formik'
import { TICKET_USAGE } from 'config/constants'
import * as Yup from 'yup'
import { isNotIntegerNumber } from 'views/utils/functions'
import { useStyles } from '../EventTicketForm.style'
import { TicketRules } from 'views/pages/EventsPage/components/TicketRules'
import { useDispatch, useSelector } from 'react-redux'
import { loadEvents, loadTicketsOfEvent } from 'state/modules/events'

const isValidArray = (ids) => {
  let isValid = true
  const idsArray = ids?.split(',')
  const arrayFiltered = idsArray?.filter((v, i) => idsArray?.indexOf(v) === i)
  if (arrayFiltered?.length < idsArray?.length) {
    isValid = false
  }
  return isValid
}

const validationSchema = () =>
  Yup.object().shape({
    Name: Yup.string()
      .required('Es requerido ingresar nombre del ticket')
      .max(250, "La cantidad máxima de caracteres es de '250'"),
    Remaining: Yup.number()
      .typeError('La cantidad debe ser un número')
      .required('Es requerido ingresar cantidad de tickets')
      .moreThan(0, 'La cantidad debe ser mayor a 0'),
    ComboTickets: Yup.string()
      .matches('^[0-9]+(?:,[0-9]+)*$', 'Formato incorrecto')
      .test('combo-tickets', 'No se permiten Ids repetidos', (ids) =>
        isValidArray(ids)
      ),
    Rules: Yup.string(),
    MaxQuantityToSale: Yup.number()
      .nullable(true)
      .integer('Solo se permiten valores enteros')
      .typeError('Solo se permiten valores numéricos')
      .min(0, 'La cantidad no puede ser menor a 0')
  })

export const TicketFreeForm = ({
  values,
  actionText,
  handleSubmit,
  onCancel,
  actionPending,
  actionSuccess
}) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const [state, setState] = useState({
    checkedC: !values.DisabledDatesForm,
    checkedD: false
  })

  const [disableFormA, setDisableFormA] = useState(values.DisabledDatesForm)

  const toggleA = (values, setFieldValue) => {
    setDisableFormA((d) => !d)
    setFieldValue('DisabledDatesForm', !values.DisabledDatesForm)
  }
  const [hideRules, setHideRules] = useState(true)

  const toggleRules = () => setHideRules((d) => !d)

  const handleChangeCheck = (event) => {
    setState({ ...state, [event.target.name]: event.target.checked })
  }

  // Being rules section
  const {
    events,
    ruleTickets: tickets,
    actionPending: pendingEvents,
    ruleTicketsPending: pendingTickets
  } = useSelector((state) => state.events)

  const fetchEvents = useCallback(async () => {
    await dispatch(loadEvents())
  }, [dispatch])

  const fetchTicketsOfEvent = useCallback(
    async (eventId) => {
      return await dispatch(loadTicketsOfEvent(eventId))
    },
    [dispatch]
  )

  useEffect(() => {
    if (!hideRules) {
      fetchEvents().then()
    }
  }, [fetchEvents, hideRules])
  // End rules section

  return (
    <Formik
      enableReinitialize
      initialValues={values}
      validateOnChange={true}
      validateOnBlur={true}
      validationSchema={validationSchema()}
      onSubmit={(values) => {
        if (hideRules) {
          values.Rules = ''
        }
        handleSubmit(values)
      }}>
      {({
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        setFieldValue,
        handleSubmit
      }) => (
        <form
          noValidate
          onSubmit={handleSubmit}
          className={classes.formContainer}>
          <div className={classes.fieldsContainer}>
            <div className={classes.actionsContainerTop}>
              <Button
                onClick={onCancel}
                disabled={actionSuccess}
                color='primary'>
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  values.DisabledDatesForm = state.checkedC
                  values.TicketUsage = TICKET_USAGE.FREE
                  handleSubmit(values)
                }}
                disabled={actionPending || actionSuccess}
                color='primary'
                variant='contained'
                startIcon={<Save />}>
                {actionPending ? (
                  <CircularProgress size={24} color='secondary' />
                ) : (
                  actionText
                )}
              </Button>
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.Name && touched.Name && errors.Name) ||
                  'Nombre del ticket (visible al usuario)'
                }
                name='Name'
                value={values.Name}
                onChange={handleChange}
                onFocus={(v) => {
                  handleBlur(v)
                }}
                error={touched.Name && Boolean(errors.Name)}
                fullWidth
                required
                size='small'
                variant='outlined'
                className={classes.field}
                autoFocus
              />
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.Remaining && touched.Remaining && errors.Remaining) ||
                  'Cantidad'
                }
                name='Remaining'
                type='string'
                value={values.Remaining}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.Remaining && Boolean(errors.Remaining)}
                fullWidth
                inputProps={{ maxLength: 9 }}
                onKeyPress={(e) => isNotIntegerNumber(e)}
                onKeyDown={(e) => isNotIntegerNumber(e)}
                required
                size='small'
                variant='outlined'
                className={classes.field}
              />
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.MaxQuantityToSale &&
                    touched.MaxQuantityToSale &&
                    errors.MaxQuantityToSale) ||
                  'Cantidad máxima de Tickets por Usuario'
                }
                name='MaxQuantityToSale'
                type='number'
                inputProps={{ maxLength: 9 }}
                value={values.MaxQuantityToSale}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.MaxQuantityToSale && Boolean(errors.MaxQuantityToSale)
                }
                fullWidth
                size='small'
                variant='outlined'
                className={classes.field}
              />
            </div>
            <div className={classes.fieldRow}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.IsTransferable}
                    onChange={handleChange}
                    name='IsTransferable'
                  />
                }
                label='Es transferible?'
              />
            </div>
            <div className={classes.fieldRow}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.Active}
                    onChange={handleChange}
                    name='Active'
                  />
                }
                label='Habilitado?'
              />
            </div>
            <div className={classes.fieldRow}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.ShowRemaining}
                    onChange={handleChange}
                    name='ShowRemaining'
                  />
                }
                label='Mostrar cantidad?'
              />
            </div>
            <div className={classes.fieldRow}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.IsOfflineEnabled}
                    onChange={handleChange}
                    name='IsOfflineEnabled'
                  />
                }
                label='Habilitar para venta Offline?'
              />
            </div>
            <div className={classes.fieldRow}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.HasSeats}
                    onChange={handleChange}
                    name='HasSeats'
                  />
                }
                label='Asignable en planimetría?'
              />
            </div>
            <div className={classes.fieldRow}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.checkedC}
                    onChange={handleChangeCheck}
                    onClick={() => {
                      toggleA(values, setFieldValue)
                    }}
                    name='checkedC'
                  />
                }
                label='Habilitar fecha de inicio y de fin de venta de ticket?'
              />
            </div>
            <span>
              {!disableFormA && (
                <div className={classes.fieldsContainer}>
                  <FormControl variant='outlined' className={classes.fieldRow}>
                    <DatePicker
                      locale='es'
                      id='SaleStartDate'
                      format='DD/MM/YYYY'
                      name='SaleStartDate'
                      value={values.SaleStartDate}
                      label={
                        (errors.SaleStartDate &&
                          touched.SaleStartDate &&
                          errors.SaleStartDate) ||
                        'Fecha de Inicio'
                      }
                      onChange={(value) => {
                        setFieldValue('SaleStartDate', value)
                      }}
                      onBlur={handleBlur}
                      error={
                        touched.SaleStartDate && Boolean(errors.SaleStartDate)
                      }
                      disableToolbar
                      className={classes.field}
                      fullWidth
                      size='small'
                      variant='inline'
                      InputLabelProps={{
                        shrink: true
                      }}
                      animateYearScrolling
                    />
                    <TimePicker
                      locale='es'
                      name='SaleStartDate'
                      className={classes.field}
                      value={values.SaleStartDate}
                      label={
                        (errors.SaleStartDate &&
                          touched.SaleStartDate &&
                          errors.SaleStartDate) ||
                        'Hora de Inicio'
                      }
                      onChange={(value) => {
                        setFieldValue('SaleStartDate', value)
                      }}
                      error={
                        touched.SaleStartDate && Boolean(errors.SaleStartDate)
                      }
                      fullWidth
                      size='small'
                      variant='inline'
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  </FormControl>
                  <FormControl variant='outlined' className={classes.fieldRow}>
                    <DatePicker
                      locale='es'
                      id='SaleEndDate'
                      format='DD/MM/YYYY'
                      name='SaleEndDate'
                      value={values.SaleEndDate}
                      label={
                        (errors.SaleEndDate &&
                          touched.SaleEndDate &&
                          errors.SaleEndDate) ||
                        'Fecha de Fin'
                      }
                      onChange={(value) => {
                        setFieldValue('SaleEndDate', value)
                      }}
                      onBlur={handleBlur}
                      error={touched.SaleEndDate && Boolean(errors.SaleEndDate)}
                      disableToolbar
                      className={classes.field}
                      fullWidth
                      size='small'
                      variant='inline'
                      InputLabelProps={{
                        shrink: true
                      }}
                      animateYearScrolling
                    />
                    <TimePicker
                      locale='es'
                      name='SaleEndDate'
                      className={classes.field}
                      value={values.SaleEndDate}
                      label={
                        (errors.SaleEndDate &&
                          touched.SaleEndDate &&
                          errors.SaleEndDate) ||
                        'Hora de Fin'
                      }
                      onChange={(value) => {
                        setFieldValue('SaleEndDate', value)
                      }}
                      error={touched.SaleEndDate && Boolean(errors.SaleEndDate)}
                      fullWidth
                      size='small'
                      variant='inline'
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  </FormControl>
                </div>
              )}
            </span>
            <div className={classes.fieldRow}>
              <FormControlLabel
                control={
                  <Checkbox
                    name={'hasRules'}
                    checked={!hideRules}
                    onClick={toggleRules}
                  />
                }
                label='Reglas?'
              />
            </div>
            <span>
              {!hideRules && (
                <div className={classes.fieldsContainer}>
                  <TicketRules
                    value={values.Rules}
                    events={events}
                    tickets={tickets}
                    pendingEvents={pendingEvents}
                    pendingTickets={pendingTickets}
                    onRequestTicketsOfEvent={(event) =>
                      fetchTicketsOfEvent(event.Id)
                    }
                    onChange={(rules) => {
                      setFieldValue('Rules', rules)
                    }}
                  />
                </div>
              )}
            </span>
          </div>
          <div className={classes.actionsContainer}>
            <Button onClick={onCancel} disabled={actionSuccess} color='primary'>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                values.DisabledDatesForm = state.checkedC
                values.TicketUsage = TICKET_USAGE.FREE
                handleSubmit(values)
              }}
              disabled={actionPending || actionSuccess}
              color='primary'
              variant='contained'
              startIcon={<Save />}>
              {actionPending ? (
                <CircularProgress size={24} color='secondary' />
              ) : (
                actionText
              )}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  )
}

export default TicketFreeForm
